import { useCallback, useEffect, useRef, type MutableRefObject } from "react";
import { useReactions } from "../hooks/useReactions";
import type { UIMessage } from "ai";
import type { Interjections, MessageMetrics, TurnPhase } from "../hooks/useChat";
import type { SessionType } from "./NewSessionDialog";
import { ChatHeader } from "./ChatHeader";
import { MessageList } from "./MessageList";
import { ChatInput } from "./ChatInput";
import { ContinueFreshPanel } from "../continue-fresh/ContinueFreshPanel";
import { AgentActivityBar } from "./AgentActivityBar";
import { useBarCollapse } from "../hooks/useBarCollapse";
import { ChevronDown, Square } from "lucide-react";
import type { ModelEntry, ChatConfig, EffortLevel } from "../shared/chat-config";
import type { PendingAttachment } from "./AttachmentPreview";
import type { AudioCache } from "../voice/audio-cache";
import type { VoiceState } from "../voice/types";

interface ChatAreaProps {
  messages: UIMessage[];
  messageMetrics?: Map<string, MessageMetrics>;
  messageOrigin?: Map<string, string>;
  messageOriginSessionLabel?: Map<string, string | null>;
  interjections?: Interjections;
  isLoading: boolean;
  connected: boolean;
  switching?: boolean;
  hasConnected?: boolean;
  error?: Error;
  sessionType: SessionType | null;
  onSend: (message: string, attachments?: PendingAttachment[]) => void;
  onStop: () => void;
  onSettingsOpen: () => void;
  /** PATH B SLICE 4A — active session's companion display name, shown in the
   *  chat header and threaded down to MessageList/ChatMessage for the
   *  assistant bubble's name + avatar. */
  companionName?: string;
  activeSessionId?: string | null;
  config: ChatConfig;
  currentModel: ModelEntry;
  availableModels: ModelEntry[];
  onModelChange: (modelId: string) => void;
  onEffortChange: (effort: EffortLevel) => void;
  onToggleThinking: () => void;
  onSpeak?: (message: UIMessage) => void;
  speakingMessageId?: string | null;
  voiceMode?: boolean;
  onToggleVoiceMode?: () => void;
  /** Current TTS playback state — used to show/hide the stop-speech button. */
  voiceState?: VoiceState;
  onStopSpeaking?: () => void;
  seekTo?: (messageId: string, fraction: number) => void;
  getPlaybackProgress?: () => number;
  audioCache?: AudioCache;
  voiceId?: string;
  playbackSpeed?: number;
  onSpeedChange?: (speed: number) => void;
  isKeepsaked?: (messageId: string) => boolean;
  onKeepsake?: (messageId: string) => void;
  scrollToMessageId?: string | null;
  onScrollToComplete?: () => void;
  sttAvailable?: boolean;
  sendSTTAudio?: (blob: Blob, format?: string) => void;
  onSTTResultRef?: MutableRefObject<((text: string) => void) | null>;
  sttStreaming?: import("../hooks/useStreamingSTT").SttStreamingProps;
  activeAgents?: Array<{ name: string; task: string; startedAt: number }>;
  /** Passes the WS send fn into useReactions so toggleReaction can fire outbound frames. */
  sendRawWs?: (payload: Record<string, unknown>) => void;
  isReplaying?: boolean;
  /** Ref wired to useReactions.mergeServerReaction — set by ChatArea, read by useChat. */
  onReactionRef?: MutableRefObject<((messageId: string, emoji: string, user: string, action: 'add' | 'remove') => void) | null>;
  /** Ref wired to useReactions.loadReactions — set by ChatArea, read by useChat. */
  onReactionSyncRef?: MutableRefObject<((reactions: Array<{ message_id: string; emoji: string; user: string }>) => void) | null>;
  /** Reactions buffered in useChat before onReactionRef was wired. Flushed here on mount. */
  pendingReactionsRef?: MutableRefObject<Array<{ messageId: string; emoji: string; user: string; action: 'add' | 'remove' }>>;
  /** reaction_sync payloads buffered in useChat before onReactionSyncRef was wired. Flushed here on mount. */
  pendingReactionSyncsRef?: MutableRefObject<Array<Array<{ message_id: string; emoji: string; user: string }>>>;
  /** When false, all reasoning/thinking blocks are hidden across the message list. */
  showReasoning?: boolean;
  turnPhase?: TurnPhase;
  turnStartedAt?: number | null;
  subprocessAlive?: boolean;
}

export function ChatArea({
  messages,
  messageMetrics = new Map(),
  messageOrigin = new Map(),
  messageOriginSessionLabel = new Map(),
  interjections,
  isLoading,
  connected,
  switching,
  hasConnected,
  error,
  sessionType,
  onSend,
  onStop,
  onSettingsOpen,
  companionName,
  activeSessionId,
  config,
  currentModel,
  availableModels,
  onModelChange,
  onEffortChange,
  onToggleThinking,
  onSpeak,
  speakingMessageId,
  voiceMode,
  onToggleVoiceMode,
  voiceState,
  onStopSpeaking,
  seekTo,
  getPlaybackProgress,
  audioCache,
  voiceId,
  playbackSpeed,
  onSpeedChange,
  isKeepsaked,
  onKeepsake,
  scrollToMessageId,
  onScrollToComplete,
  sttAvailable,
  sendSTTAudio,
  onSTTResultRef,
  sttStreaming,
  activeAgents = [],
  sendRawWs,
  onReactionRef,
  onReactionSyncRef,
  pendingReactionsRef,
  pendingReactionSyncsRef,
  isReplaying = false,
  showReasoning,
  turnPhase,
  turnStartedAt,
  subprocessAlive = true,
}: ChatAreaProps) {
  const { barExpanded, collapseBar, toggleBar, expandBar, setScrollContainer, prefersReducedMotion, isScrolledAway, scrollToLatest } =
    useBarCollapse();
  const { reactions, toggleReaction, mergeServerReaction, loadReactions, sendReactionRef } = useReactions();

  // Focus return: when speaking stops, return focus to the chat input if the active
  // element was the stop button (or body after it unmounts).
  const prevVoiceStateRef = useRef<VoiceState | undefined>(voiceState);
  useEffect(() => {
    const prev = prevVoiceStateRef.current;
    prevVoiceStateRef.current = voiceState;
    if (prev === 'speaking' && voiceState !== 'speaking') {
      const active = document.activeElement;
      if (!active || active === document.body || (active as HTMLElement).classList.contains('tts-stop-btn')) {
        const input = document.querySelector<HTMLElement>('.chat-input-field');
        input?.focus();
      }
    }
  }, [voiceState]);

  // Wire the WS send function into useReactions so toggleReaction can send outbound frames.
  useEffect(() => {
    if (!sendRawWs) return;
    sendReactionRef.current = (payload) => sendRawWs(payload as Record<string, unknown>);
  }, [sendRawWs, sendReactionRef]);

  // Wire useChat's reaction callback refs to useReactions handlers so incoming
  // reaction frames from the gateway are merged into local state.
  // After wiring, flush any reactions that arrived during the race window before
  // this effect ran (pendingReactionsRef is populated by useChat when ref is null).
  useEffect(() => {
    if (onReactionRef) {
      onReactionRef.current = mergeServerReaction;
      if (pendingReactionsRef) {
        const pending = pendingReactionsRef.current.splice(0);
        for (const r of pending) {
          console.log('[ChatArea] flushing pending reaction:', r.messageId, r.emoji, r.user, r.action);
          mergeServerReaction(r.messageId, r.emoji, r.user, r.action);
        }
      }
    }
  }, [onReactionRef, mergeServerReaction, pendingReactionsRef]);

  useEffect(() => {
    if (onReactionSyncRef) {
      onReactionSyncRef.current = loadReactions;
      if (pendingReactionSyncsRef) {
        const pending = pendingReactionSyncsRef.current.splice(0);
        for (const batch of pending) {
          console.log('[ChatArea] flushing pending reaction_sync:', batch.length, 'reactions');
          loadReactions(batch);
        }
      }
    }
  }, [onReactionSyncRef, loadReactions, pendingReactionSyncsRef]);

  // Pass the scroll container directly to useBarCollapse so the scroll listener
  // re-attaches when MessageList mounts (ref-only approach ran the effect before
  // the DOM element existed, so the listener was never registered).
  const handleContainerRef = useCallback((el: HTMLDivElement | null) => {
    setScrollContainer(el);
  }, [setScrollContainer]);

  // Derive connection status for the header pill
  const connectionStatus = switching
    ? "switching" as const
    : connected
      ? "connected" as const
      : hasConnected
        ? "reconnecting" as const
        : "connecting" as const;

  return (
    <div className="chat-area">
      {/* Persistent screen-reader status region for TTS state changes */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {voiceState === 'speaking' ? 'Speaking' : ''}
      </div>
      <ChatHeader
        connectionStatus={connectionStatus}
        sessionType={sessionType}
        onSettingsOpen={onSettingsOpen}
        companionName={companionName}
      />
      <AgentActivityBar agents={activeAgents} />
      <MessageList
        messages={messages}
        messageMetrics={messageMetrics}
        messageOrigin={messageOrigin}
        messageOriginSessionLabel={messageOriginSessionLabel}
        queuedMessages={interjections?.queued}
        onTakeBack={interjections?.onTakeBack}
        availableModels={availableModels}
        isLoading={isLoading}
        error={error}
        companionName={companionName}
        sessionId={activeSessionId}
        onSpeak={onSpeak}
        speakingMessageId={speakingMessageId}
        seekTo={seekTo}
        getPlaybackProgress={getPlaybackProgress}
        audioCache={audioCache}
        voiceId={voiceId}
        playbackSpeed={playbackSpeed}
        onSpeedChange={onSpeedChange}
        onContainerRef={handleContainerRef}
        reactions={reactions}
        onReact={toggleReaction}
        isKeepsaked={isKeepsaked}
        onKeepsake={onKeepsake}
        scrollToMessageId={scrollToMessageId}
        onScrollToComplete={onScrollToComplete}
        onSendMessage={onSend}
        isReplaying={isReplaying}
        showReasoning={showReasoning}
        turnPhase={turnPhase}
        turnStartedAt={turnStartedAt}
        subprocessAlive={subprocessAlive}
      />
      {isScrolledAway && messages.length > 0 && (
        <button
          className="scroll-to-latest"
          onClick={scrollToLatest}
          aria-label="Scroll to latest messages"
        >
          <ChevronDown size={18} />
        </button>
      )}
      {voiceState === "speaking" && onStopSpeaking && (
        <div className="tts-stop-bar">
          <button
            className="tts-stop-btn"
            onClick={onStopSpeaking}
            aria-label="Stop speaking"
            title="Stop speaking"
          >
            <Square size={12} fill="currentColor" />
            <span>Stop speaking</span>
          </button>
        </div>
      )}
      <ContinueFreshPanel />
      <ChatInput
        onSend={onSend}
        onStop={onStop}
        takeBackDraft={interjections?.takeBackDraft ?? null}
        isLoading={isLoading}
        disabled={!connected}
        switching={switching}
        config={config}
        currentModel={currentModel}
        availableModels={availableModels}
        onModelChange={onModelChange}
        onEffortChange={onEffortChange}
        onToggleThinking={onToggleThinking}
        activeSessionId={activeSessionId}
        voiceMode={voiceMode}
        onToggleVoiceMode={onToggleVoiceMode}
        onStopSpeaking={onStopSpeaking}
        barExpanded={barExpanded}
        onToggleBar={toggleBar}
        onCollapseBar={collapseBar}
        onExpandBar={expandBar}
        prefersReducedMotion={prefersReducedMotion}
        sttAvailable={sttAvailable}
        sendSTTAudio={sendSTTAudio}
        onSTTResultRef={onSTTResultRef}
        sttStreaming={sttStreaming}
      />
    </div>
  );
}
